import styled from 'styled-components/macro';
import { BaseReactComponent, Color, MuiPaletteColor } from '../../../types';
import { Typography, SvgIconProps, lighten } from '@mui/material';

export interface HiTagProps extends BaseReactComponent {
  /**
   * The content of the component.
   */
  label: string;
  /**
   * The color of the component. It supports theme colors and custom colors.
   */
  color?: MuiPaletteColor | Color;
  /**
   * Icon element placed before the label.
   */
  icon?: React.ComponentType<SvgIconProps>;
}

/**
 * The Tag component is only used to **categorize items**, it doesn’t allow any action.
 * Use tags when content is mapped to multiple categories, and the user needs a way to differentiate between them.
 *
 * - Use short labels for easy scanning.
 * - Use more than 1 word only if necessary to describe the status and differentiate it from other tags.
 *
 * You can use any color you want for a tag.
 */
const HiTag = ({ label, color, icon: IconComponent, ...other }: HiTagProps) => {
  return (
    <StyledTag {...other} color={color}>
      {IconComponent && <IconComponent className="HiTag-icon" />}
      <Typography className="HiTag-label" variant="body3">
        {label}
      </Typography>
    </StyledTag>
  );
};

const StyledTag = styled('div').attrs(({ theme, color = 'primary' }) => ({
  color: Object.prototype.hasOwnProperty.call(theme.palette, color)
    ? theme.palette[color].main
    : color
}))`
  display: inline-flex;

  padding: 2px 8px;
  border-radius: 2px;

  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  background-color: ${({ color }) => lighten(color, 0.9)};

  .HiTag-icon {
    margin-left: -2px;
    margin-right: 4px;
    font-size: 16px;
    color: ${({ color }) => color};
  }
  .HiTag-label {
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    color: ${({ color }) => color};
  }
`;

export default HiTag;
